import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	AtlBrowserStorageLocal,
	AtlBrowserStorageSession,
} from '@atlassian/browser-storage-controls/web-storage-service';
import { fg } from '@atlassian/jira-feature-gating';
import { STOREJS_KEY_PREFIX } from './constants.tsx';
import {
	type SetItemResult,
	type WebStorageProvider,
	type WebStorageProviderNew,
	WebStorageType,
} from './types.tsx';

import { isStorageAPIAvailable } from './isStorageAPIAvailable.tsx';

const LOG_LOCATION = 'platform.browser-storage-providers.create-storage-provider';

// Keyless operations carry no UGC, so they use the privacy-safe logger.
const logError = (operation: string, error: unknown): void => {
	if (fg('jfp-vulcan-browser-storage-error-logs')) {
		log.safeErrorWithoutCustomerData(
			LOG_LOCATION,
			`Failed to ${operation}`,
			error instanceof Error ? error : undefined,
		);
	} else {
		// eslint-disable-next-line no-console
		console.error(`Failed to ${operation}`, error);
	}
};

// Storage keys may contain UGC (issue/project keys), so keyed operations use the
// customer-data logger, which routes to the restricted frontend-exception pipeline.
const logKeyedError = (operation: string, key: string, error: unknown): void => {
	if (fg('jfp-vulcan-browser-storage-error-logs')) {
		log.unsafeErrorWithCustomerData(
			LOG_LOCATION,
			`Failed to ${operation} (key: ${key})`,
			error instanceof Error ? error : undefined,
		);
	} else {
		// eslint-disable-next-line no-console
		console.error(`Failed to ${operation} (key: ${key})`, error);
	}
};

export const createStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix: string | undefined,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
): WebStorageProvider | WebStorageProviderNew => {
	const normalizedAppPrefix = appPrefix ?? '';
	const namespacedStore = store.namespace(normalizedAppPrefix);
	// Uses the same key format as store.js: namespace ? '__storejs_' + namespace + '_' + key : key
	const getKeyWithPrefix = (key: string) =>
		normalizedAppPrefix.trim().length ? `${STOREJS_KEY_PREFIX}${normalizedAppPrefix}_${key}` : key;

	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;

	const browserStorage =
		storageType === WebStorageType.LocalStorage
			? globalThis.localStorage
			: globalThis.sessionStorage;

	const remove = (key: string) => {
		try {
			if (fg('jfp-vulcan-browser-storage-migration')) {
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				storageAPI.removeItem(prefixedKey); // remove data from new store
				namespacedStore.remove(key); // remove data from old store as well
			} else {
				namespacedStore.remove(key);
			}
		} catch (err) {
			logKeyedError('remove item', key, err);
		}
	};

	return {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any) => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.setItem(prefixedKey, JSON.stringify(value)).then((setItemResult) => {
						if (setItemResult?.status === 'BLOCKED') {
							storageAPI.removeItem(prefixedKey);
						}
					});
				} else {
					namespacedStore.set(key, value);
				}
			} catch (err) {
				logKeyedError('set item', key, err);
			}
		},
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setAsync: async (key: string, value: any): Promise<void> => {
			try {
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					const setItemResult: SetItemResult = await storageAPI.setItem(
						prefixedKey,
						JSON.stringify(value),
					);
					if (setItemResult !== undefined) {
						if (setItemResult.status === 'BLOCKED') {
							storageAPI.removeItem(prefixedKey);
							throw new Error('write was blocked by storage or consent preferences.');
						}
						if (setItemResult.status !== 'SUCCESS') {
							throw new Error('write failed');
						}
					}
				} else {
					namespacedStore.set(key, value);
				}
			} catch (err) {
				logKeyedError('set item', key, err);
				throw err;
			}
		},

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any) => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.setStrictlyNecessaryItem(prefixedKey, JSON.stringify(value));
				} else {
					// this has to be done when setStrictlyNecessaryItem comes into usage, and it should have a fallback when FG is turned off
					namespacedStore.set(key, value);
				}
			} catch (err) {
				logKeyedError('set strictly necessary item', key, err);
			}
		},
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						return undefined;
					}
					const prefixedKey = getKeyWithPrefix(key);
					const item = storageAPI.getItem(prefixedKey);
					if (typeof item === 'string') {
						return JSON.parse(item);
					}
				} else {
					return namespacedStore.get(key);
				}
			} catch (err) {
				logKeyedError('get item', key, err);
			}
		},
		remove,
		// Clears the complete storage
		clearAll: () => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						return;
					}
					storageAPI.clear();
					// clean up old keys too
					namespacedStore.clearAll();
				} else {
					namespacedStore.clearAll();
				}
			} catch (err) {
				logError('clear storage', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					return 0;
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					return storageAPI.length();
				}
				return browserStorage?.length || 0;
			} catch (err) {
				logError('get storage length', err);
				return 0;
			}
		},
		key: (index: number): string | null => {
			try {
				if (!isStorageAPIAvailable()) {
					return null;
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					return storageAPI.key(index);
				}
				return browserStorage?.key(index) ?? null;
			} catch (err) {
				logError('get key at index', err);
				return null;
			}
		},
	};
};
